import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { getDataFromCSV } from './components/getDataFromCSV';
import styles from './Main.module.css';
import { useHistory, useLocation } from 'react-router-dom';
import { isMobileOnly, isMobile } from 'react-device-detect';
import {
  AllInputDataContext,
  CalculatedDataContext,
  SliderValuesContext,
  SetSliderValuesContext,
  UserLinkContext,
} from './config';
import Controls from './components/controls/Controls';
import MECharts from './components/MEcharts/MECharts';
import runCalculation from './algorithm/RunCalculation';
import getQueryVariable from '../../functions/getQueryVariable';
import { updateAddressBar } from './functions/updateAddressBar';
import { useHistoryHandeller } from '../../useEffects/useHistoryHandeller';
import { useIsLandscape } from '../../useEffects/useIsLandscape';

/* eslint-disable import/no-webpack-loader-syntax */

// import worker from 'workerize-loader?inline!./algorithm/calculation.worker.js'
// const workerInstance = worker()

const Main = () => {
  const location = useLocation();

  const {
    sliderValues: sliderValuesFromQuery = 'invalid',
    country: countryFromQuery = 'aus',
    year: yearFromQuery = 'current',
    version: versionFromQuery = 'educational',
    page: pageFromQuery = 'charts',
    welcome: welcomeFromQuery = false,
  } = useMemo(() => getQueryVariable(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const allInputDataNEW = useRef({});
  const [calculatedData, setCalculatedData] = useState({});
  const [sliderValues, setSliderValues] = useState();
  const sliderValuesFromDBorQuery = useRef(sliderValuesFromQuery);
  const initalFetch = useRef(true);
  const [dataRequested, setDataRequested] = useState(
    location.pathname.toLowerCase() === '/megs'
      ? {
          dataset: 'megs',
          year: 2050,
          country: 'aus',
        }
      : {
          dataset: 'esx',
          year: yearFromQuery,
          country: countryFromQuery,
        }
  );

  const [navRequired, setNavRequired] = useState(false);
  const [version, setVersion] = useState(versionFromQuery);
  const firstRunValues = useRef();
  const apiFetchComplete = useRef(false);
  const [contentToDisplay, setContentToDisplay] = useState(pageFromQuery);
  const [pageMode, setPageMode] = useState(
    pageFromQuery !== 'home' && pageFromQuery !== 'charts'
  );
  const [userLink, setUserLink] = useState();
  const [MEGSFilteringParameters, setMEGSFilteringParameters] = useState([]);
  const isLandscape = useIsLandscape();
  const skipWelcome = welcomeFromQuery === 'skip';
  const [controlView, setControlView] = useState(
    skipWelcome ? 'slim' : 'full-screen'
  ); //full-screen | vertical | slim

  //Welcome Journey
  const [welcomeJourneyStep, setWelcomeJourneyStep] = useState(
    skipWelcome ? 99 : 1
  );

  // 1: Build grid
  // 2: Here's the job
  // 3: Animate to controls
  // 4: Welcome to the team
  // 5: Build my power grid
  // 6: Tour
  //7: Finish tour
  //99: done

  const processWelcomeJourney = (curr) =>
    setWelcomeJourneyStep(Number(curr) ? Number(curr) + 1 : (prev) => prev + 1);

  //Welcome Journey END
  useEffect(() => {
    if (welcomeJourneyStep === 5) processWelcomeJourney();
  }, [welcomeJourneyStep]);
  //!isMobile || isLandscape

  useEffect(() => {
    if (controlView !== 'full-screen' && isMobileOnly) {
      setControlView(isLandscape ? 'vertical' : 'slim');
    }
  }, [isLandscape, setControlView]);

  const history = useHistory();
  const reqUpdateAddressBar = useRef();
  useHistoryHandeller({
    history,
    sliderValues,
    setSliderValues,
    dataRequested,
    setDataRequested,
    setVersion,
    setContentToDisplay,
    setPageMode,
    reqUpdateAddressBar,
  });

  const initiateCalculation = useCallback(
    async (initialData, sliderValues, firstRunValues) => {
      setLoading(true);
      try {
        const results = runCalculation({
          initialData,
          sliderValues,
          firstRunValues,
        });

        setCalculatedData(results);
      } catch (error) {
        setError('Calculation Failed', error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchInitialData = useCallback(async ({ dataRequested }) => {
    setLoading(true);

    try {
      // const initialDataDB = await getDataFromDB({ dataRequested });
      const initialDataCSV = await getDataFromCSV();

      const initialData = initialDataCSV;

      if (
        sliderValuesFromDBorQuery.current === 'invalid' ||
        !initalFetch.current
      ) {
        sliderValuesFromDBorQuery.current = initialData.technologyData.map(
          (a) => +a.sliderStart
        );
      }
      if (initalFetch.current) {
        const queryVaribles = getQueryVariable();

        setMEGSFilteringParameters(
          Object.keys(queryVaribles).reduce((acc, fp) => {
            const index = initialData.megsTechnologies.findIndex((mt) => {
              return mt.techName.toLowerCase() === fp.toLowerCase();
            });
            if (index === -1) return acc;

            return [
              ...acc,
              {
                ...initialData.megsTechnologies[index],
                values: queryVaribles[fp],
              },
            ];
          }, [])
        );
      }
      allInputDataNEW.current = initialData;
      initalFetch.current = false;
      firstRunValues.current = { firstRun: true };

      setSliderValues(sliderValuesFromDBorQuery.current);

      apiFetchComplete.current = true;
    } catch (error) {
      setError('Get Data Failed', error);
    } finally {
    }
  }, []);
  useEffect(() => {
    updateAddressBar({
      reqUpdateAddressBar,
      setUserLink,
      sliderValues,
      dataRequested,
      version,
      contentToDisplay,
      pageMode,
      MEGSFilteringParameters,
    });
  }, [
    sliderValues,
    dataRequested,
    version,
    contentToDisplay,
    pageMode,
    MEGSFilteringParameters,
  ]);

  useEffect(() => {
    //Saving First Run Results
    firstRunValues.current = calculatedData.firstRunValues;
  }, [calculatedData.firstRunValues]);

  useEffect(() => {
    // Runs the calculation on Slider Update or new API Data
    if (apiFetchComplete.current === true) {
      initiateCalculation(
        allInputDataNEW.current,
        sliderValues,
        firstRunValues.current
      );
    }
  }, [initiateCalculation, sliderValues]);

  useEffect(() => {
    //Runs the API fetch
    // console.log('API CALL', dataRequested);
    fetchInitialData({ dataRequested });
  }, [dataRequested, fetchInitialData]);

  useEffect(() => {
    if (error !== null) {
      // console.log('#### ERROR ####');
      // console.log('Error Message:', error);
      // console.log('sliderValues', sliderValues);
      // console.log('dataRequested', dataRequested);
      // console.log('allInputData', allInputDataNEW.current);
      // console.log('#### ERROR ####');
    }
  }, [dataRequested, error, sliderValues]);

  const handleVersionSwitch = useCallback(() => {
    if (version === 'educational') {
      setVersion('scientific');
      return;
    }
    setVersion('educational');
  }, [setVersion, version]);

  const siteConditionalStyle = () => {
    if (dataRequested.dataset === 'megs')
      if (navRequired && isMobileOnly && pageMode)
        // backgroundColor = 'linear-gradient(333deg, #c95766 23%, #800000 59%)';
        return {
          // backgroundColor,
          height: '100vh',
          overflowY: 'hidden',
          paddingTop: '350px',
        };
    if (isMobileOnly && pageMode)
      return { height: '100vh', overflowY: 'hidden' };
    if (navRequired && isMobileOnly) return { paddingTop: '350px' };
    if (!isMobile && pageMode) return { overflowY: 'hidden' };
    return {};
  };
  if (contentToDisplay === 'home') return <p>Old Main</p>;
  if (loading)
    return (
      <div className={styles.preLoadMessage}>
        Setting up grid infrastructure
      </div>
    );
  if (error !== null)
    return <div className={styles.preLoadMessage}>Error: {error}</div>;

  return (
    <AllInputDataContext.Provider value={allInputDataNEW.current}>
      <CalculatedDataContext.Provider value={calculatedData}>
        <SetSliderValuesContext.Provider value={setSliderValues}>
          <SliderValuesContext.Provider value={sliderValues}>
            <UserLinkContext.Provider value={userLink}>
              <div className={styles.site} style={siteConditionalStyle()}>
                <Controls
                  navRequired={navRequired}
                  setNavRequired={setNavRequired}
                  setDataRequested={setDataRequested}
                  dataRequested={dataRequested}
                  version={version}
                  setContentToDisplay={setContentToDisplay}
                  setPageMode={setPageMode}
                  handleVersionSwitch={handleVersionSwitch}
                  MEGSFilteringParameters={MEGSFilteringParameters}
                  setMEGSFilteringParameters={setMEGSFilteringParameters}
                  controlView={controlView}
                  setControlView={setControlView}
                  welcomeJourneyStep={welcomeJourneyStep}
                  processWelcomeJourney={processWelcomeJourney}
                />

                <div
                  className={styles.pageContainerCharts}
                  style={{
                    top: pageMode ? '100vh' : '0px',
                    display: welcomeJourneyStep < 5 ? 'none' : 'block',
                  }}
                >
                  <MECharts
                    version={version}
                    welcomeJourneyStep={welcomeJourneyStep}
                    setWelcomeJourneyStep={setWelcomeJourneyStep}
                    processWelcomeJourney={processWelcomeJourney}
                  />
                </div>
              </div>
            </UserLinkContext.Provider>
          </SliderValuesContext.Provider>
        </SetSliderValuesContext.Provider>
      </CalculatedDataContext.Provider>
    </AllInputDataContext.Provider>
  );
};

// App.whyDidYouRender = {
//   logOnDifferentValues: true,
//   customName: 'Apps'
// }

export default Main;
